import uploadIcon from "assets/img/icon-upload.svg"

import { FC, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs, { Dayjs } from "utils/dayjs"

import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import AssetMaintenance from "models/AssetMaintenance"
import Form from "components/Form/Form"
import CustomError from "models/CustomError"
import { Guid, ICreateFile, IValidationRule } from "@/types"
import ValidationService from "services/ValidationService"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import FormField from "components/FormField/FormField"
import useMaintenanceTypes from "hooks/useMaintenanceTypes"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import { Grid, GridItem } from "components/Grid"
import InputDateTime from "components/InputDateTime/InputDateTime"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import InputNumber from "components/InputNumber/InputNumber"
import NoValue from "components/NoValue/NoValue"
import Input from "components/Input/Input"
import { DOCUMENT_INPUT_FILE_ACCEPT, DOCUMENT_SUPPORTED_EXTENSIONS, DOCUMENT_SUPPORTED_TYPES, DOCUMENT_SUPPORTED_TYPES_LABEL, DOCUMENT_UPLOAD_MAX_SIZE_MB, INPUT_MAX_LENGTH_LARGE } from "config/constants"
import Checkbox from "components/Checkbox/Checkbox"
import DropFiles from "components/DropFiles/DropFiles"
import DroppableArea from "components/DroppableArea/DroppableArea"
import fileUtils from "utils/file"
import Button from "components/Button/Button"
import AddDocumentsDocument from "components/AddDocumentsModal/AddDocumentsDocument"
import { Flex } from "components/Flex"
import FileService from "services/FileService"

interface IMaintenanceCompleteModalProps {
    maintenance: AssetMaintenance
    onClose: () => void
    onCompleted: () => Promise<void>
}

const MaintenanceCompleteModal: FC<IMaintenanceCompleteModalProps> = ({
    maintenance,
    onClose,
    onCompleted,
}) => {
    const { t } = useTranslation()
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [followUpThresholdDate, setFollowUpThresholdDate] = useState(maintenance?.thresholdDate || null)
    const [followUpThresholdOperationHours, followUpThresholdOperationHoursNumber, setFollowUpThresholdOperationHours] = useInputNumber(maintenance?.thresholdOperationHours || "")
    const [completedDate, setCompletedDate] = useState<Dayjs | null>(maintenance?.completedDate || dayjs().utc().startOf('day'))
    const [scheduleFollowUpMaintenance, setScheduleFollowUpMaintenance] = useState(false)
    const [comment, setComment] = useState(maintenance.completedComment || "")
    const [fileToUpload, setFileToUpload] = useState<File | null>(null)
    const [fileError, setFileError] = useState("")
    const [isLoadingFile, setIsLoadingFile] = useState(false)
    const inputFileEl = useRef<HTMLInputElement>(null)

    const isFollowUpThresholdDateRequired = scheduleFollowUpMaintenance && !followUpThresholdOperationHoursNumber
    const isFollowUpThresholdOperationHoursRequired = scheduleFollowUpMaintenance && !followUpThresholdDate

    const { maintenanceTypesMap } = useMaintenanceTypes()
    const typeName = maintenanceTypesMap.get(maintenance.typeId)?.name

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        const validationFields: [string, IValidationRule, any][] = [
            ["followUpThresholdDate", { required: isFollowUpThresholdDateRequired }, followUpThresholdDate],
            ["followUpThresholdOperationHours", { required: isFollowUpThresholdOperationHoursRequired, min: 0, type: "integer" }, followUpThresholdOperationHours],
        ]
        return ValidationService.validateMultiple(validationFields)
    }, [followUpThresholdDate, followUpThresholdOperationHours, isFollowUpThresholdDateRequired, isFollowUpThresholdOperationHoursRequired])


    function handleInputFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files ? Array.from(event.target.files) : null
        event.target.value = ""
        if (!files || !files.length) return
        handleFilesSelect(files)
    }

    async function handleFilesSelect(files: File[]) {
        setFileError("")

        const file = files[0]
        if (!file) return

        try {
            setIsLoadingFile(true)
            await fileUtils.validateDocumentFile({
                file: file,
                extensions: DOCUMENT_SUPPORTED_EXTENSIONS,
                types: DOCUMENT_SUPPORTED_TYPES,
                maxSizeMb: DOCUMENT_UPLOAD_MAX_SIZE_MB,
            })
            setFileToUpload(file)
        } catch(e) {
            if (e instanceof CustomError) {
                setFileError(e.message)
            } else {
                setFileError(t("validation.failedToReadFile"))
            }
            setFileToUpload(null)
        } finally {
            setIsLoadingFile(false)
        }
    }

    async function handleComplete() {
        setIsSubmitted(true)

        if (Object.keys(validationErrors).length > 0
            || !completedDate
            || !maintenance?.linkComplete) {
            return
        }

        setIsSubmitting(true)

        try {
            let fileId: Guid | null = null
            if (fileToUpload) {
                const uploadedFile = await FileService.uploadFile<ICreateFile>(fileToUpload)
                fileId = uploadedFile.id
            }

            await AssetService.completeAssetMaintenance(maintenance.linkComplete, {
                date: completedDate,
                comment,
                fileId,
                followUpMaintenance: scheduleFollowUpMaintenance ? {
                    typeId: maintenance.typeId,
                    thresholdDate: followUpThresholdDate,
                    thresholdOperationHours: followUpThresholdOperationHoursNumber || null,
                } : undefined,
            })
            await onCompleted()
            onClose()
        } catch(e) {
            toastMessages.showSystemError()
            setIsSubmitting(false)
        }
    }

    return (
        <ConfirmModal
            title={t('maintenance.completeMaintenance')}
            maxWidth="708px"
            onClose={onClose}
            onConfirm={handleComplete}
            confirmButtonLabel={t('maintenance.completeMaintenance')}
            isBusy={isSubmitting}>
            <Form onSubmit={handleComplete} className="py-1">
                {!!typeName &&
                    <div className="font-bold mb-2">{typeName}</div>
                }
                <Grid columns="1fr 1fr" gap="2.4rem">
                    <GridItem>
                        <FormField
                            label={t('maintenance.thresholdDate')}
                            isText>
                            {maintenance.thresholdDate
                                ? <div className="font-bold">{maintenance.thresholdDate.format(t('config.dateLongFormat'))}</div>
                                : <NoValue />
                            }
                        </FormField>
                    </GridItem>
                    <GridItem>
                        <FormField
                            label={t('maintenance.thresholdOperatingHours')}
                            isText>
                            {maintenance.thresholdOperationHours
                                ? <div className="font-bold">{t('common.xHours', { count: maintenance.thresholdOperationHours })}</div>
                                : <NoValue />
                            }
                        </FormField>
                    </GridItem>
                    <GridItem>
                        <FormField
                            label={t('maintenance.maintenanceCompletedOn')}
                            isText>
                            <InputDateTime
                                type="date"
                                date={completedDate}
                                utc
                                onDateChange={setCompletedDate}
                                disabled={isSubmitting}
                            />
                        </FormField>
                    </GridItem>
                </Grid>
                <FormField
                    label={t('maintenance.confirmationDocument')}
                    className="mt-3"
                    optional>
                    {fileToUpload
                        ? <AddDocumentsDocument
                            file={fileToUpload}
                            onDelete={() => setFileToUpload(null)}
                            isDisabled={isSubmitting}
                        />
                        : <DropFiles onDrop={handleFilesSelect} enabled={!isSubmitting && !isLoadingFile}>
                            {({ isDragOver }) => (
                                <DroppableArea isDragOver={isDragOver}>
                                    <div className="add-documents-modal__drop add-documents-modal__drop--small">

                                        <Flex className="mb-2" alignItems="flex-start" gap="0.8rem">
                                            <img src={uploadIcon} className="mr-1" alt="" />
                                            {t("addDocuments.dropDocumentsHere")} ({DOCUMENT_SUPPORTED_TYPES_LABEL})
                                        </Flex>
                                        <Button
                                            theme="secondary"
                                            label={t("addDocuments.chooseDocument")}
                                            onClick={() => inputFileEl.current?.click()}
                                            loading={isLoadingFile}
                                            disabled={isLoadingFile || isSubmitting}
                                        />

                                        <input
                                            type="file"
                                            accept={DOCUMENT_INPUT_FILE_ACCEPT.join(",")}
                                            className="add-documents-modal__input-file"
                                            onChange={handleInputFileChange}
                                            ref={inputFileEl}
                                            data-testid="add-documents-input-file" />

                                        {!!fileError &&
                                            <div className="mt-2">
                                                <ValidationMessage>{fileError}</ValidationMessage>
                                            </div>
                                        }
                                    </div>
                                </DroppableArea>
                            )}
                        </DropFiles>
                    }
                </FormField>
                <FormField
                    label={t('common.comment')}
                    className="my-3"
                    optional>
                    <Input
                        type="textarea"
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        disabled={isSubmitting}
                        maxLength={INPUT_MAX_LENGTH_LARGE}
                        rows={6}
                    />
                </FormField>
                <Checkbox
                    checked={scheduleFollowUpMaintenance}
                    onChange={() => setScheduleFollowUpMaintenance(current => !current)}
                    label={t('maintenance.followUpMaintenance')}
                />
                {scheduleFollowUpMaintenance &&
                    <div className="mt-1-5 pl-4">
                        {!!typeName &&
                            <div className="font-bold">{typeName}</div>
                        }
                        <Grid columns="1fr 1fr" gap="2.4rem">
                            <GridItem>
                                <FormField
                                    label={t('maintenance.thresholdDate')}
                                    optional={!isFollowUpThresholdDateRequired}>
                                    <InputDateTime
                                        type="date"
                                        date={followUpThresholdDate}
                                        utc
                                        onDateChange={setFollowUpThresholdDate}
                                        isInvalid={isSubmitted && !!validationErrors.followUpThresholdDate}
                                        disabled={isSubmitting}
                                        clearButton
                                    />
                                    {isSubmitted && !!validationErrors.followUpThresholdDate &&
                                        <ValidationMessage>{validationErrors.followUpThresholdDate.message}</ValidationMessage>
                                    }
                                </FormField>
                            </GridItem>
                            <GridItem>
                                <FormField
                                    label={t('maintenance.thresholdOperatingHours')}
                                    optional={!isFollowUpThresholdOperationHoursRequired}>
                                    <InputNumber
                                        value={followUpThresholdOperationHours}
                                        onChange={setFollowUpThresholdOperationHours}
                                        isSubmitted={isSubmitted}
                                        isInvalid={!!validationErrors.followUpThresholdOperationHours}
                                        disabled={isSubmitting}
                                        after={<div className="px-2">{t('common.hours')}</div>}
                                    />
                                    {isSubmitted && !!validationErrors.followUpThresholdOperationHours &&
                                        <ValidationMessage>{validationErrors.followUpThresholdOperationHours.message}</ValidationMessage>
                                    }
                                </FormField>
                            </GridItem>
                        </Grid>
                    </div>
                }
            </Form>
        </ConfirmModal>
    )
}

export default MaintenanceCompleteModal
